import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  IconButton,
  Tooltip,
  MenuItem,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Person as PersonIcon,
  Note as NoteIcon,
  AttachFile as AttachFileIcon,
  Payment as PaymentIcon,
  Email as EmailIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  NavigateNext,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { Claim } from '../types/api';
import { Notes } from './Notes';
import Attachments from './Attachments';
import Communications from './Communications';
import PaymentsTab from './claims/PaymentsTab';
import ClaimProgressTracker from './claims/ClaimProgressTracker';
import InsuredsTab from './claims/InsuredsTab';
import api from '../utils/axios';
import LocationMap from './LocationMap';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`claim-tabpanel-${index}`}
      aria-labelledby={`claim-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ClaimDetails: React.FC = () => {
  const { fileNumber } = useParams<{ fileNumber: string }>();
  const [claim, setClaim] = useState<Claim | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editValues, setEditValues] = useState<any>({});
  const [editClaimInline, setEditClaimInline] = useState(false);
  const [editInsurerInline, setEditInsurerInline] = useState(false);
  const [insurerOptions, setInsurerOptions] = useState<{ id: string; name: string }[]>([]);
  const [representativeOptions, setRepresentativeOptions] = useState<{ id: string; firstName: string; lastName: string; email: string | null; phone: string | null }[]>([]);
  const [insurerEdit, setInsurerEdit] = useState<{ insurerId: string; policyNumber: string; claimNumber: string; adjusterName: string; representativeId: string; representativeEmail: string; representativePhone: string }>({ 
    insurerId: '', 
    policyNumber: '', 
    claimNumber: '', 
    adjusterName: '', 
    representativeId: '',
    representativeEmail: '',
    representativePhone: ''
  });
  const [editInsuredInline, setEditInsuredInline] = useState(false);
  const [insuredEdit, setInsuredEdit] = useState<{ insuredName: string; clientId: string | null }>({ insuredName: '', clientId: null });
  const [clientOptions, setClientOptions] = useState<{ id: string; type: string; firstName?: string; lastName?: string; companyName?: string; email?: string; phone?: string; address?: string }[]>([]);
  const [userOptions, setUserOptions] = useState<{ id: string; firstName: string; lastName: string; email: string; role: string }[]>([]);
  const [claimEdit, setClaimEdit] = useState<{ 
    description: string; 
    status: string; 
    type: string; 
    dateOfLoss: string; 
    dateReported: string; 
    claimAddress: string; 
    latitude: number | null; 
    longitude: number | null;
    publicAdjusterId: string | null;
  }>({ 
    description: '', 
    status: '', 
    type: '', 
    dateOfLoss: '', 
    dateReported: '', 
    claimAddress: '', 
    latitude: null, 
    longitude: null,
    publicAdjusterId: null
  });

  const navigate = useNavigate();

  useEffect(() => {
    if (fileNumber) {
      loadClaim(fileNumber);
    }
  }, [fileNumber]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/insurers/dropdown');
        if (res.data.success) {
          const sorted = (res.data.data as any[]).sort((a, b) => a.name.localeCompare(b.name));
          setInsurerOptions(sorted.map(i => ({ id: i.id, name: i.name })));
        }
      } catch (err) {
        console.error('Failed to load insurers', err);
      }
    })();
  }, []);

  // Function to fetch clients for dropdown
  const fetchClients = async () => {
    try {
              const response = await api.get('/clients?limit=1000');
      if (response.data.success) {
        const sorted = (response.data.data as any[])
          .sort((a: any, b: any) => {
            const nameA = a.type === 'COMPANY' 
              ? a.companyName 
              : `${a.firstName || ''} ${a.lastName || ''}`.trim();
            const nameB = b.type === 'COMPANY' 
              ? b.companyName 
              : `${b.firstName || ''} ${b.lastName || ''}`.trim();
            return nameA.localeCompare(nameB);
          });
        setClientOptions(sorted);
      }
    } catch (err) {
      console.error('Failed to load clients', err);
      setClientOptions([]);
    }
  };

  // Function to fetch users for Public Adjuster dropdown
  const fetchUsers = async () => {
    try {
      const response = await api.get('/users/adjusters');
      if (response.data.success) {
        const sorted = (response.data.data as any[])
          .sort((a: any, b: any) => {
            const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim();
            const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim();
            return nameA.localeCompare(nameB);
          });
        setUserOptions(sorted);
      }
    } catch (err) {
      console.error('Failed to load users', err);
      setUserOptions([]);
    }
  };

  // Function to fetch insurance representatives for a specific insurer
  const fetchRepresentatives = async (insurerId: string) => {
    if (!insurerId) {
      setRepresentativeOptions([]);
      return;
    }
    
    try {
              const response = await api.get(`/insurance-reps?insurerId=${insurerId}&limit=100`);
      if (response.data.success) {
        const sorted = (response.data.data as any[])
          .sort((a: any, b: any) => {
            const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
            const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
            return nameA.localeCompare(nameB);
          });
        setRepresentativeOptions(sorted.map((rep: any) => ({
          id: rep.id,
          firstName: rep.firstName,
          lastName: rep.lastName,
          email: rep.email,
          phone: rep.phone
        })));
      }
    } catch (err) {
      console.error('Failed to load insurance representatives', err);
      setRepresentativeOptions([]);
    }
  };

  const loadClaim = async (fileNumber: string) => {
    try {
      setLoading(true);
      setError(null);
              const response = await api.get(`/claims/by-file-number/${fileNumber}`);
      console.log('Claim data received:', response.data);
      if (response.data.success) {
        setClaim(response.data.data);
        console.log('Claim state updated:', response.data.data);
        console.log('Coordinates:', response.data.data.latitude, response.data.data.longitude);
      } else {
        setError('Failed to load claim');
      }
    } catch (err) {
      console.error('Error loading claim:', err);
      setError('Failed to load claim');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Handlers for editing
  const openEditDialog = () => {
    if (!claim) return;
    setEditValues({
      description: claim.description || '',
      status: claim.status || '',
      type: claim.type || '',
              dateOfLoss: claim.dateOfLoss ? claim.dateOfLoss.substring(0, 10) : '',
        dateReported: claim.dateReported ? claim.dateReported.substring(0, 10) : '',
        amount: claim.amount ?? 0,
        claimAddress: claim.claimAddress || claim.location || '',
        latitude: claim.latitude,
        longitude: claim.longitude,
    });
    setEditDialogOpen(true);
  };

  const handleEditChange = (field: string, value: any) => {
    setEditValues((prev: any) => ({ ...prev, [field]: value }));
  };

  const saveEdit = async () => {
    if (!claim) return;
    try {
      // Convert date strings to proper ISO DateTime format
      const formatDateForAPI = (dateString: string | null) => {
        if (!dateString) return null;
        // If it's already a full ISO string, return as is
        if (dateString.includes('T') || dateString.includes('Z')) {
          return dateString;
        }
        // If it's just a date string (YYYY-MM-DD), convert to ISO
        return new Date(dateString + 'T00:00:00.000Z').toISOString();
      };

      const payload = {
        ...editValues,
        dateOfLoss: formatDateForAPI(editValues.dateOfLoss),
        dateReported: formatDateForAPI(editValues.dateReported),
        amount: Number(editValues.amount) || 0,
        latitude: editValues.latitude,
        longitude: editValues.longitude,
      };
      
      console.log('Sending payload with formatted dates:', payload);
      
      const response = await api.put(`/claims/${claim.id}`, payload);
      if (response.data.success) {
        setClaim(response.data.data);
        setEditDialogOpen(false);
      } else {
        alert(response.data.message || 'Update failed');
      }
    } catch (err) {
      console.error('Error updating claim:', err);
      console.error('Error response:', err.response?.data);
      alert('Failed to update claim');
    }
  };

  // Inline save for Claim Details section
  const saveClaimInline = async () => {
    if (!claim) return;
    try {
      // Ensure userOptions is loaded
      if (!userOptions || userOptions.length === 0) {
        await fetchUsers();
      }
      
      // Convert date strings to proper ISO DateTime format
      const formatDateForAPI = (dateString: string | null) => {
        if (!dateString) return null;
        // If it's already a full ISO string, return as is
        if (dateString.includes('T') || dateString.includes('Z')) {
          return dateString;
        }
        // If it's just a date string (YYYY-MM-DD), convert to ISO
        return new Date(dateString + 'T00:00:00.000Z').toISOString();
      };

      // Build payload with all claim details including dates
      const payload: any = {
        description: claimEdit.description,
        status: claimEdit.status,
        type: claimEdit.type,
        dateOfLoss: formatDateForAPI(claimEdit.dateOfLoss),
        dateReported: formatDateForAPI(claimEdit.dateReported),
        claimAddress: claimEdit.claimAddress,
        latitude: claimEdit.latitude,
        longitude: claimEdit.longitude,
      };
      
      // Only add adjusterName if we have valid user data
      if (claimEdit.publicAdjusterId && userOptions && userOptions.length > 0) {
        const selectedUser = userOptions.find(u => u.id === claimEdit.publicAdjusterId);
        if (selectedUser && selectedUser.firstName && selectedUser.lastName) {
          payload.adjusterName = `${selectedUser.firstName} ${selectedUser.lastName}`.trim();
        }
      }
      
      // Remove any undefined or null values from payload
      Object.keys(payload).forEach(key => {
        if (payload[key] === undefined || payload[key] === null || payload[key] === '') {
          delete payload[key];
        }
      });
      
      console.log('Sending claim update payload:', payload);
      console.log('Claim ID:', claim.id);
      
      const response = await api.put(`/claims/${claim.id}`, payload, {
        timeout: 10000 // 10 second timeout
      });
      
      console.log('Response received:', response);
      console.log('Response data:', response.data);
      
      if (response.data.success) {
        console.log('Updating claim state with:', response.data.data);
        setClaim(response.data.data);
        setEditClaimInline(false);
      } else {
        alert(response.data.message || 'Update failed');
      }
    } catch (err: any) {
      console.error('Error updating claim:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      console.error('Error message:', err.message);
      console.error('Error code:', err.code);
      console.error('Error config:', err.config);
      
      // Show more specific error message
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update claim';
      alert(`Error updating claim: ${errorMessage}`);
    }
  };

  const saveInsurerInline = async () => {
    if (!claim) return;
    try {
      const payload = {
        insurerId: insurerEdit.insurerId,
        policyNumber: insurerEdit.policyNumber,
        claimNumber: insurerEdit.claimNumber,
        representativeId: insurerEdit.representativeId || null
      };
      const response = await api.put(`/claims/${claim.id}`, payload);
      if (response.data.success) {
        setClaim(response.data.data);
        setEditInsurerInline(false);
      } else {
        alert(response.data.message || 'Update failed');
      }
    } catch (err) {
      console.error(err);
      alert('Failed to update insurer details');
    }
  };

  const saveInsuredInline = async () => {
    if (!claim) return;
    try {
      const payload = { 
        insuredName: insuredEdit.insuredName,
        clientId: insuredEdit.clientId || null
      };
      const response = await api.put(`/claims/${claim.id}`, payload);
      if (response.data.success) {
        setClaim(response.data.data);
        setEditInsuredInline(false);
      } else {
        alert(response.data.message || 'Update failed');
      }
    } catch (err) {
      console.error(err);
      alert('Failed to update insured details');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!claim) {
    return (
      <Box p={3}>
        <Alert severity="info">Claim not found</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumb */}
      <Breadcrumbs 
        separator={<NavigateNext fontSize="small" />} 
        sx={{ mb: 2 }}
      >
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/claims')}
          sx={{ cursor: 'pointer' }}
        >
          Claims
        </Link>
        <Typography color="text.primary">{claim.file_number}</Typography>
      </Breadcrumbs>

      <Paper sx={{ borderRadius: 2, position: 'relative' }}>
        <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
          <Button variant="contained" size="small" onClick={openEditDialog}>Edit</Button>
        </Box>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          aria-label="claim tabs"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<DescriptionIcon />} iconPosition="start" label="Overview" />
          <Tab icon={<PaymentIcon />} iconPosition="start" label="Payments" />
          <Tab icon={<TimelineIcon />} iconPosition="start" label="Progress" />
          <Tab icon={<PersonIcon />} iconPosition="start" label="Insureds" />
          <Tab icon={<NoteIcon />} iconPosition="start" label="File Notes" />
          <Tab icon={<AttachFileIcon />} iconPosition="start" label="Attachments" />
          <Tab icon={<EmailIcon />} iconPosition="start" label="Communications" />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(12, 1fr)', 
            gap: 3,
            '& > *': { minHeight: 'fit-content' }
          }}>
            {/* Row 1: First 3 sections - each takes 4 columns */}
            <Box sx={{ gridColumn: 'span 4' }}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent sx={{ position: 'relative', height: '100%' }}>
                  <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                    {editInsuredInline ? (
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Save">
                          <IconButton size="small" color="primary" onClick={saveInsuredInline}>
                            <CheckIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton size="small" color="inherit" onClick={() => setEditInsuredInline(false)}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ) : (
                      <Tooltip title="Edit Insured Details">
                        <IconButton size="small" onClick={async () => {
                          await fetchClients();
                          setInsuredEdit({ 
                            insuredName: claim.insuredName || '', 
                            clientId: claim.clientId || null 
                          });
                          setEditInsuredInline(true);
                        }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    Insured Details
                  </Typography>
                  
                  {editInsuredInline ? (
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField
                        select
                        label="Connect to Client"
                        fullWidth
                        value={insuredEdit.clientId || ''}
                        onChange={(e) => setInsuredEdit({...insuredEdit, clientId: e.target.value || null})}
                      >
                        <MenuItem value="">
                          <em>No client connection</em>
                        </MenuItem>
                        {clientOptions.map(client => (
                          <MenuItem key={client.id} value={client.id}>
                            {client.type === 'COMPANY' 
                              ? client.companyName 
                              : `${client.firstName || ''} ${client.lastName || ''}`.trim()
                            }
                            {client.email && ` (${client.email})`}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField 
                        label="Insured Name (Manual)" 
                        fullWidth 
                        value={insuredEdit.insuredName} 
                        onChange={(e) => setInsuredEdit({...insuredEdit, insuredName: e.target.value})} 
                      />
                    </Box>
                  ) : (
                    <Box sx={{ mt: 2 }}>
                      {/* Personal Information Subsection */}
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon fontSize="small" />
                          Personal Information
                        </Typography>
                        <Box sx={{ pl: 2 }}>
                          <Typography>
                            <strong>Insured Name:</strong> {
                              claim.client 
                                ? claim.client.type === 'COMPANY' 
                                  ? claim.client.companyName 
                                  : `${claim.client.firstName || ''} ${claim.client.lastName || ''}`.trim()
                                : claim.insuredName || 'Not provided'
                            }
                          </Typography>
                          {claim.client && (
                            <Typography>
                              <strong>Client Type:</strong> {claim.client.type}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {/* Contact Details Subsection */}
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EmailIcon fontSize="small" />
                          Contact Details
                        </Typography>
                        <Box sx={{ pl: 2 }}>
                          <Typography>
                            <strong>Email:</strong> {claim.client?.email || 'Not provided'}
                          </Typography>
                          <Typography>
                            <strong>Phone:</strong> {claim.client?.phone || 'Not provided'}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Address Information Subsection */}
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocationIcon fontSize="small" />
                          Address Information
                        </Typography>
                        <Box sx={{ pl: 2 }}>
                          <Typography>
                            <strong>Home Address:</strong> {claim.client?.address || 'Not provided'}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ gridColumn: 'span 4' }}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent sx={{ position: 'relative', height: '100%' }}>
                  <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                    {editInsurerInline ? (
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Save">
                          <IconButton size="small" color="primary" onClick={saveInsurerInline}>
                            <CheckIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton size="small" color="inherit" onClick={() => setEditInsurerInline(false)}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ) : (
                      <Tooltip title="Edit Insurer Details">
                        <IconButton size="small" onClick={async () => {
                          const initialInsurerId = claim.insurerId || '';
                          setInsurerEdit({
                            insurerId: initialInsurerId,
                            policyNumber: claim.policyNumber || '',
                            claimNumber: claim.claimNumber || '',
                            adjusterName: claim.adjusterName || '',
                            representativeId: claim.representative?.id || '',
                            representativeEmail: claim.representative?.email || '',
                            representativePhone: claim.representative?.phone || ''
                          });
                          setEditInsurerInline(true);
                          // Fetch representatives for the current insurer
                          await fetchRepresentatives(initialInsurerId);
                        }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    Insurer Details
                  </Typography>
                  <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {editInsurerInline ? (
                      <>
                        <TextField 
                          select 
                          label="Insurance Company" 
                          fullWidth 
                          value={insurerEdit.insurerId} 
                          onChange={async (e) => {
                            const newInsurerId = e.target.value;
                            setInsurerEdit({...insurerEdit, insurerId: newInsurerId, representativeId: ''});
                            await fetchRepresentatives(newInsurerId);
                          }}
                        >
                          {insurerOptions.map(opt => (
                            <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
                          ))}
                        </TextField>
                        <TextField 
                          select 
                          label="Insurance Representative" 
                          fullWidth 
                          value={insurerEdit.representativeId} 
                          onChange={(e) => {
                            const selectedRep = representativeOptions.find(rep => rep.id === e.target.value);
                            setInsurerEdit({
                              ...insurerEdit, 
                              representativeId: e.target.value,
                              representativeEmail: selectedRep ? selectedRep.email || '' : '',
                              representativePhone: selectedRep ? selectedRep.phone || '' : ''
                            });
                          }}
                        >
                          <MenuItem value="">
                            <em>Select a representative</em>
                          </MenuItem>
                          {representativeOptions.map(rep => (
                            <MenuItem key={rep.id} value={rep.id}>
                              {`${rep.firstName} ${rep.lastName}`}
                              {rep.email && ` (${rep.email})`}
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField 
                          label="Representative Email" 
                          fullWidth 
                          type="email"
                          value={insurerEdit.representativeEmail} 
                          onChange={(e) => setInsurerEdit({...insurerEdit, representativeEmail: e.target.value})} 
                        />
                        <TextField 
                          label="Representative Phone" 
                          fullWidth 
                          type="tel"
                          value={insurerEdit.representativePhone} 
                          onChange={(e) => setInsurerEdit({...insurerEdit, representativePhone: e.target.value})} 
                        />
                        <TextField label="Claim Rep (Manual)" fullWidth value={insurerEdit.adjusterName} onChange={(e)=>setInsurerEdit({...insurerEdit, adjusterName:e.target.value})} />
                        <TextField label="Policy Number" fullWidth value={insurerEdit.policyNumber} onChange={(e)=>setInsurerEdit({...insurerEdit, policyNumber:e.target.value})} />
                        <TextField label="Claim Number" fullWidth value={insurerEdit.claimNumber} onChange={(e)=>setInsurerEdit({...insurerEdit, claimNumber:e.target.value})} />
                      </>
                    ) : (
                      <Box sx={{ mt: 2 }}>
                        {/* Insurance Company Info Subsection */}
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle2" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BusinessIcon fontSize="small" />
                            Insurance Company Info
                          </Typography>
                          <Box sx={{ pl: 2 }}>
                            <Typography>
                              <strong>Insurance Company:</strong> {claim.insurer?.name || claim.insurerName || 'Not provided'}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Policy Information Subsection */}
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle2" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DescriptionIcon fontSize="small" />
                            Policy Information
                          </Typography>
                          <Box sx={{ pl: 2 }}>
                            <Typography>
                              <strong>Policy Number:</strong> {claim.policyNumber || 'Not provided'}
                            </Typography>
                            <Typography>
                              <strong>Claim Number:</strong> {claim.claimNumber || 'Not provided'}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Representative Details Subsection */}
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle2" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon fontSize="small" />
                            Representative Details
                          </Typography>
                          <Box sx={{ pl: 2 }}>
                            <Typography>
                              <strong>Insurance Representative:</strong> {claim.representative ? `${claim.representative.firstName} ${claim.representative.lastName || ''}`.trim() : 'Not provided'}
                            </Typography>
                            {claim.representative && (
                              <>
                                <Typography>
                                  <strong>Email:</strong> {claim.representative.email || 'Not provided'}
                                </Typography>
                                <Typography>
                                  <strong>Phone:</strong> {claim.representative.phone || 'Not provided'}
                                </Typography>
                              </>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ gridColumn: 'span 4' }}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent sx={{ position: 'relative', height: '100%' }}>
                  <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                    {editClaimInline ? (
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Save">
                          <IconButton size="small" color="primary" onClick={saveClaimInline}>
                            <CheckIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton size="small" color="inherit" onClick={() => setEditClaimInline(false)}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ) : (
                      <Tooltip title="Edit Claim Details">
                        <IconButton size="small" onClick={async () => {
                          await fetchUsers();
                          const currentAdjuster = userOptions.find(user => 
                            `${user.firstName} ${user.lastName}`.trim() === claim.adjusterName?.trim()
                          );
                          console.log('Current adjuster name:', claim.adjusterName);
                          console.log('Found adjuster:', currentAdjuster);
                          setClaimEdit({
                            description: claim.description || '',
                            status: claim.status || '',
                            type: claim.type || '',
                            dateOfLoss: claim.dateOfLoss?.substring(0,10) || '',
                            dateReported: claim.dateReported?.substring(0,10) || '',
                            claimAddress: claim.claimAddress || claim.location || '',
                            latitude: claim.latitude,
                            longitude: claim.longitude,
                            publicAdjusterId: currentAdjuster?.id || null
                          });
                          setEditClaimInline(true);
                        }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    Claim Details
                  </Typography>
                  <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {editClaimInline ? (
                      <>
                        <TextField
                          label="Description"
                          fullWidth
                          multiline
                          minRows={2}
                          value={claimEdit.description}
                          onChange={(e) => setClaimEdit({...claimEdit, description: e.target.value})}
                        />
                        <TextField
                          label="Status"
                          fullWidth
                          value={claimEdit.status}
                          onChange={(e) => setClaimEdit({...claimEdit, status: e.target.value})}
                        />
                        <TextField
                          label="Type"
                          fullWidth
                          value={claimEdit.type}
                          onChange={(e) => setClaimEdit({...claimEdit, type: e.target.value})}
                        />
                        <TextField
                          select
                          label="Public Adjuster"
                          fullWidth
                          value={claimEdit.publicAdjusterId || ''}
                          onChange={(e) => setClaimEdit({...claimEdit, publicAdjusterId: e.target.value || null})}
                        >
                          <MenuItem value="">
                            <em>No Public Adjuster assigned</em>
                          </MenuItem>
                          {userOptions.filter(user => user.role === 'ADJUSTER').map(user => (
                            <MenuItem key={user.id} value={user.id}>
                              {`${user.firstName} ${user.lastName}`}
                              {user.email && ` (${user.email})`}
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          label="Date of Loss"
                          type="date"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          value={claimEdit.dateOfLoss}
                          onChange={(e) => setClaimEdit({...claimEdit, dateOfLoss: e.target.value})}
                        />
                        <TextField
                          label="Date Reported"
                          type="date"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          value={claimEdit.dateReported}
                          onChange={(e) => setClaimEdit({...claimEdit, dateReported: e.target.value})}
                        />
                        <TextField
                          label="Latitude"
                          type="number"
                          fullWidth
                          value={claimEdit.latitude ?? ''}
                          onChange={e => setClaimEdit({...claimEdit, latitude: parseFloat(e.target.value)})}
                        />
                        <TextField
                          label="Longitude"
                          type="number"
                          fullWidth
                          value={claimEdit.longitude ?? ''}
                          onChange={e => setClaimEdit({...claimEdit, longitude: parseFloat(e.target.value)})}
                        />
                        <Box mt={2}>
                          <LocationMap
                            latitude={claimEdit.latitude}
                            longitude={claimEdit.longitude}
                            address={claim.location}
                            title="Set Claim Location"
                            height={250}
                            editable
                            onLocationSelect={(lat, lng) => {
                              setClaimEdit({...claimEdit, latitude: lat, longitude: lng});
                            }}
                          />
                        </Box>
                      </>
                    ) : (
                      <Box sx={{ mt: 2 }}>
                        {/* Basic Claim Info Subsection */}
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle2" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DescriptionIcon fontSize="small" />
                            Basic Claim Info
                          </Typography>
                          <Box sx={{ pl: 2 }}>
                            <Typography>
                              <strong>Description:</strong> {claim.description}
                            </Typography>
                            <Typography>
                              <strong>Type:</strong> {claim.type}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Dates & Timeline Subsection */}
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle2" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarIcon fontSize="small" />
                            Dates & Timeline
                          </Typography>
                          <Box sx={{ pl: 2 }}>
                            <Typography>
                              <strong>Date of Loss:</strong> {new Date(claim.dateOfLoss).toLocaleDateString()}
                            </Typography>
                            <Typography>
                              <strong>Date Reported:</strong> {new Date(claim.dateReported).toLocaleDateString()}
                            </Typography>
                            <Typography>
                              <strong>Created:</strong> {new Date(claim.createdAt).toLocaleDateString()}
                            </Typography>
                            <Typography>
                              <strong>Last Updated:</strong> {new Date(claim.updatedAt).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Status & Progress Subsection */}
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle2" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon fontSize="small" />
                            Status & Progress
                          </Typography>
                          <Box sx={{ pl: 2 }}>
                            <Typography>
                              <strong>Status:</strong> {claim.status}
                            </Typography>
                            <Typography>
                              <strong>Public Adjuster:</strong> {
                                claim.adjusterName || 'Not assigned'
                              }
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Row 2: Location and Payment Summary - each takes 6 columns */}
            <Box sx={{ gridColumn: 'span 6' }}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="h6" gutterBottom>
                      Claim Address & Location
                    </Typography>
                    <Tooltip title="Edit Location">
                      <IconButton size="small" onClick={() => {
                        setEditValues({
                          description: claim.description || '',
                          status: claim.status || '',
                          type: claim.type || '',
                          dateOfLoss: claim.dateOfLoss ? claim.dateOfLoss.substring(0, 10) : '',
                          dateReported: claim.dateReported ? claim.dateReported.substring(0, 10) : '',
                          amount: claim.amount ?? 0,
                          claimAddress: claim.claimAddress || claim.location || '',
                          latitude: claim.latitude,
                          longitude: claim.longitude,
                        });
                        setEditDialogOpen(true);
                      }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Typography>
                    <strong>Claim Address:</strong> {claim.claimAddress || claim.location || 'Not provided'}
                  </Typography>
                  {claim.location && claim.location !== claim.claimAddress && (
                    <Typography>
                      <strong>Original Location:</strong> {claim.location}
                    </Typography>
                  )}
                  <Box mt={2}>
                    <LocationMap
                      latitude={claim.latitude}
                      longitude={claim.longitude}
                      address={claim.claimAddress || claim.location}
                      title="Claim Location"
                      height={200}
                      showAddress={false}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ gridColumn: 'span 6' }}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Payment Summary
                  </Typography>
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(2, 1fr)', 
                    gap: 2,
                    mt: 2
                  }}>
                    {/* Prior Payments */}
                    <Card variant="outlined" sx={{ bgcolor: '#f0f8ff', height: '100%' }}>
                      <CardContent sx={{ py: 2, px: 2 }}>
                        <Typography variant="subtitle2" color="primary" gutterBottom>
                          Prior Payments
                        </Typography>
                        <Typography variant="h6" color="primary" fontWeight="bold">
                          ${((claim.priorUndisputedPayments || 0) + (claim.otherUndisputedPayments || 0) + (claim.otherPayments || 0)).toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Prior: ${(claim.priorUndisputedPayments || 0).toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Other: ${(claim.otherUndisputedPayments || 0).toLocaleString()}
                        </Typography>
                      </CardContent>
                    </Card>

                    {/* Total Settlement */}
                    <Card variant="outlined" sx={{ bgcolor: '#f6ffed', height: '100%' }}>
                      <CardContent sx={{ py: 2, px: 2 }}>
                        <Typography variant="subtitle2" color="success.main" gutterBottom>
                          Total Settlement
                        </Typography>
                        <Typography variant="h6" color="success.main" fontWeight="bold">
                          ${(claim.totalSettlement || 0).toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Net: ${((claim.totalSettlement || 0) - (claim.deductibles || 0) - (claim.expenses || 0)).toLocaleString()}
                        </Typography>
                      </CardContent>
                    </Card>

                    {/* Expenses & Deductibles */}
                    <Card variant="outlined" sx={{ bgcolor: '#fff7e6', height: '100%' }}>
                      <CardContent sx={{ py: 2, px: 2 }}>
                        <Typography variant="subtitle2" color="warning.main" gutterBottom>
                          Expenses & Deductibles
                        </Typography>
                        <Typography variant="h6" color="warning.main" fontWeight="bold">
                          ${((claim.expenses || 0) + (claim.deductibles || 0)).toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Expenses: ${(claim.expenses || 0).toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Deductibles: ${(claim.deductibles || 0).toLocaleString()}
                        </Typography>
                      </CardContent>
                    </Card>

                    {/* PA Commission */}
                    <Card variant="outlined" sx={{ bgcolor: '#f9f0ff', height: '100%' }}>
                      <CardContent sx={{ py: 2, px: 2 }}>
                        <Typography variant="subtitle2" color="secondary" gutterBottom>
                          PA Commission
                        </Typography>
                        <Typography variant="h6" color="secondary" fontWeight="bold">
                          ${(claim.paPayment || 0).toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Fee: {claim.paFeePercentage || 0}%
                        </Typography>
                      </CardContent>
                    </Card>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <PaymentsTab claim={claim} />
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <ClaimProgressTracker claim={claim} />
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <InsuredsTab client={claim.client} currentClaimId={claim.id} />
        </TabPanel>

        <TabPanel value={activeTab} index={4}>
          <Notes claimId={claim.id} />
        </TabPanel>

        <TabPanel value={activeTab} index={5}>
          <Attachments claimId={claim.id} />
        </TabPanel>

        <TabPanel value={activeTab} index={6}>
          <Communications claimId={claim.id} />
        </TabPanel>
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Claim</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Description"
              fullWidth
              multiline
              minRows={2}
              value={editValues.description}
              onChange={(e) => handleEditChange('description', e.target.value)}
            />
            <TextField
              label="Status"
              fullWidth
              value={editValues.status}
              onChange={(e) => handleEditChange('status', e.target.value)}
            />
            <TextField
              label="Type"
              fullWidth
              value={editValues.type}
              onChange={(e) => handleEditChange('type', e.target.value)}
            />
            <TextField
              label="Date of Loss"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={editValues.dateOfLoss}
              onChange={(e) => handleEditChange('dateOfLoss', e.target.value)}
            />
            <TextField
              label="Date Reported"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={editValues.dateReported}
              onChange={(e) => handleEditChange('dateReported', e.target.value)}
            />
            <TextField
              label="Amount"
              type="number"
              fullWidth
              value={editValues.amount}
              onChange={(e) => handleEditChange('amount', e.target.value)}
            />
            <TextField
              label="Latitude"
              type="number"
              fullWidth
              value={editValues.latitude ?? ''}
              onChange={e => handleEditChange('latitude', parseFloat(e.target.value))}
            />
            <TextField
              label="Claim Address"
              fullWidth
              value={editValues.claimAddress ?? ''}
              onChange={e => handleEditChange('claimAddress', e.target.value)}
            />
            <TextField
              label="Longitude"
              type="number"
              fullWidth
              value={editValues.longitude ?? ''}
              onChange={e => handleEditChange('longitude', parseFloat(e.target.value))}
            />
            <Box mt={2}>
              <LocationMap
                latitude={editValues.latitude}
                longitude={editValues.longitude}
                address={editValues.claimAddress || claim.location}
                title="Set Claim Location"
                height={250}
                editable
                onLocationSelect={(lat, lng) => {
                  handleEditChange('latitude', lat);
                  handleEditChange('longitude', lng);
                }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveEdit}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClaimDetails; 